import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

let lastDialogPrimaryAt = 0

const DialogContent = React.forwardRef(({ className, children, onKeyDown, ...props }, ref) => {
  const innerRef = React.useRef(null)
  const setRefs = (node) => {
    innerRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }
  // Enter = trigger the dialog's primary action (last button in DialogFooter).
  // Esc closes the dialog (Radix default — handled by not preventing onEscapeKeyDown).
  const triggerPrimary = (e) => {
    // Enter acts as the dialog's confirm key (clicks the primary footer button). Ignore
    // auto-repeat (held key).
    if (e.key !== "Enter" || e.shiftKey || e.repeat || e.nativeEvent?.isComposing) return
    // Multi-line fields keep Enter for newlines.
    if (e.target?.tagName === "TEXTAREA") return
    const content = innerRef.current
    if (!content) return
    const footer = content.querySelector("[data-dialog-footer]")
    if (!footer) return
    const btns = footer.querySelectorAll("button")
    const primary = btns[btns.length - 1]
    if (!primary || primary.disabled) return
    // Always swallow the native Enter so a button that opens another dialog can't also be
    // activated natively. We then fire the click ourselves, gated by a global cooldown so a
    // single Enter can't cascade into a dialog that this very Enter just opened
    // (e.g. detail → Update Status, or a row → confirmation dialog).
    e.preventDefault()
    const now = Date.now()
    if (now - lastDialogPrimaryAt < 700) return
    lastDialogPrimaryAt = now
    primary.click()
  }
  const handleKeyDown = (e) => {
    onKeyDown?.(e)
    if (e.defaultPrevented) return
    triggerPrimary(e)
  }
  // When auto-focus is prevented (e.g. remarks-only dialogs), nothing inside the dialog holds
  // focus, so the content-level onKeyDown never fires. A document-level listener covers that
  // case. Only the top-most dialog acts, and only when focus is outside the content.
  React.useEffect(() => {
    const onDocKey = (e) => {
      const content = innerRef.current
      if (!content || content.contains(e.target)) return
      const dialogs = document.querySelectorAll("[role=\"dialog\"]")
      if (dialogs.length && dialogs[dialogs.length - 1] !== content) return
      triggerPrimary(e)
    }
    document.addEventListener("keydown", onDocKey)
    return () => document.removeEventListener("keydown", onDocKey)
  }, [])
  // Mobile keyboard handling: when the soft keyboard shrinks the visible viewport, anchor the
  // dialog to the top of the visible area and cap its height so the focused field and footer
  // stay reachable (the dialog scrolls internally). Reset to centered when the keyboard closes.
  React.useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const apply = () => {
      const node = innerRef.current
      if (!node) return
      const smallScreen = window.innerWidth < 640
      const keyboardOpen = window.innerHeight - vv.height > 150
      if (smallScreen && keyboardOpen) {
        node.style.top = `${vv.offsetTop + 8}px`
        node.style.transform = "translateX(-50%)"
        node.style.maxHeight = `${vv.height - 16}px`
      } else {
        node.style.top = ""
        node.style.transform = ""
        node.style.maxHeight = ""
      }
    }
    apply()
    vv.addEventListener("resize", apply)
    vv.addEventListener("scroll", apply)
    return () => {
      vv.removeEventListener("resize", apply)
      vv.removeEventListener("scroll", apply)
    }
  }, [])
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 max-h-[90dvh] overflow-y-auto shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    data-dialog-footer=""
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
